import React from "react";
import { motion } from "framer-motion";
import {
  TrendingUp, Activity, Sparkles, Calculator, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Area, AreaChart } from "recharts";

const sparkData = [
  { v: 12 }, { v: 18 }, { v: 14 }, { v: 22 }, { v: 28 }, { v: 24 }, { v: 32 }, { v: 38 }, { v: 36 }, { v: 44 },
];

// Card positions are responsive: shown on lg+ screens only (absolute around bull),
// on smaller screens we fall back to a horizontal scrolling row below the bull.
export default function FloatingHeroCards({ inlineForMobile = false }) {
  const cards = [
    {
      key: "portfolio",
      pos: "left-[2%] top-[6%]",
      delay: 0.2,
      mobileOrder: 1,
      content: (
        <div data-testid="hero-card-portfolio">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-sky-500/15 border border-sky-500/30 flex items-center justify-center">
                <TrendingUp className="w-3.5 h-3.5 text-sky-400" />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Portfolio</div>
            </div>
            <span className="text-[10px] text-emerald-400 font-medium">YTD</span>
          </div>
          <div className="mt-3 font-[Outfit] text-2xl text-white font-light tracking-tight">₹12,84,500</div>
          <div className="mt-1 flex items-center gap-1 text-xs text-emerald-400">
            <ArrowUpRight className="w-3.5 h-3.5" /> +18.6%
          </div>
          <div className="-mx-1 mt-2 h-9 opacity-90">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData}>
                <defs>
                  <linearGradient id="hc-p" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke="#0EA5E9" strokeWidth={1.6} fill="url(#hc-p)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      ),
    },
    {
      key: "forex",
      pos: "right-[2%] top-[10%]",
      delay: 0.5,
      mobileOrder: 2,
      content: (
        <div data-testid="hero-card-forex">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <Activity className="w-3.5 h-3.5 text-emerald-400" />
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Forex</div>
            </div>
            <span className="text-[10px] text-emerald-400">Live</span>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-[Outfit] text-base text-slate-300">EUR/USD</span>
          </div>
          <div className="font-[Outfit] text-2xl text-white font-light tracking-tight">1.0842</div>
          <div className="mt-1 flex items-center gap-1 text-xs text-emerald-400">
            <ArrowUpRight className="w-3.5 h-3.5" /> +0.32%
          </div>
          <div className="-mx-1 mt-2 h-9 opacity-90">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparkData}>
                <Line type="monotone" dataKey="v" stroke="#10B981" strokeWidth={1.6} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ),
    },
    {
      key: "insight",
      pos: "left-[3%] bottom-[8%]",
      delay: 0.8,
      mobileOrder: 3,
      content: (
        <div data-testid="hero-card-insight">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">AI Insight</div>
          </div>
          <div className="mt-3 text-sm text-white leading-snug">
            “Rebalance suggested: <span className="text-emerald-400">↑ Tech</span> <span className="text-rose-400">↓ Commodities</span>”
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-[10px] text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> AI engine · just now
          </div>
        </div>
      ),
    },
    {
      key: "sip",
      pos: "right-[3%] bottom-[10%]",
      delay: 1.1,
      mobileOrder: 4,
      content: (
        <div data-testid="hero-card-sip">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
              <Calculator className="w-3.5 h-3.5 text-violet-400" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">SIP Calculator</div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Monthly</div>
              <div className="font-[Outfit] text-base text-white font-light">₹10,000</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Est. Return</div>
              <div className="font-[Outfit] text-base text-[#D4AF37] font-light">₹32.18 L</div>
            </div>
          </div>
          <div className="mt-2.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full w-3/4 bg-gradient-to-r from-violet-500 to-[#D4AF37]" />
          </div>
        </div>
      ),
    },
  ];

  if (inlineForMobile) {
    return (
      <div className="lg:hidden mt-8 -mx-6 px-6 overflow-x-auto scrollbar-hide" data-testid="hero-cards-mobile">
        <div className="flex gap-3 snap-x snap-mandatory">
          {cards.sort((a, b) => a.mobileOrder - b.mobileOrder).map((c) => (
            <div key={c.key} className="snap-start min-w-[240px] glass border border-white/10 rounded-2xl p-4">
              {c.content}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="hidden lg:block absolute inset-0 pointer-events-none" data-testid="hero-cards-desktop">
      {cards.map((c, i) => (
        <motion.div
          key={c.key}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: c.delay, ease: "easeOut" }}
          className={`absolute ${c.pos} pointer-events-auto`}
        >
          <motion.div
            animate={{ y: [0, i % 2 === 0 ? -10 : 10, 0] }}
            transition={{ duration: 5 + i, repeat: Infinity, ease: "easeInOut" }}
            className="glass border border-white/10 rounded-2xl p-4 w-60 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
          >
            {c.content}
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
}
